import { useEffect, useCallback, useState } from "react";
import { flatten } from "flat";
import moment from "moment";

export const useOrderFormLogic = ({
  visible,
  type,
  activeOrder,
  orderServiceTypes,
  form,
  onFormChange,
  setStartDate,
  setEndDate,
  setNoEndDate,
  selectedClient,
  selectedCategory,
  startDate,
  endDate,
  changedValues,
}) => {
  const [isSupportingCommunity, setIsSupportingCommunity] = useState(true);
  const [isDayCenter, setIsDayCenter] = useState(false);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
    }
  }, [visible]);

  useEffect(() => {
    // Attempt to find the current service type from the list of available types using the active order's service type ID.
    const serviceType = orderServiceTypes?.find(
      (i) => i.id === activeOrder.order_service_type_id
    );
    // Get the name of the service type if it exists; otherwise, default to null.
    const serviceTypeName = serviceType ? serviceType.name : null;
    // Check if there's an active order and proceed if it exists.
    if (activeOrder?.id) {
      // Determine if the form is being used to duplicate an order based on the 'type' prop.
      const isDuplicate = type === "duplicate";
      // Flatten the active order object.
      const flattenOrder = flatten(activeOrder, { safe: true });

      // Set the form values using the flattened order details.
      // Also, apply default values based on whether it's a duplicate form and the service type name.
      form.setFieldsValue({
        ...flattenOrder,
        ...getFormDefaults(isDuplicate, flattenOrder, serviceTypeName),
      });

      // Set the start date state. If duplicating an order, this will be null; otherwise, it's the order's start date.
      setStartDate(isDuplicate ? null : activeOrder.effective_from);
      // Set the end date state. If duplicating an order, this will be null; otherwise, it's the order's end date.
      setEndDate(isDuplicate ? null : activeOrder.effective_to);
      // Determine whether there's no end date based on the duplicate status and the presence of an effective end date in the order.
      setNoEndDate(isDuplicate || !activeOrder.effective_to);
    }
  }, [
    activeOrder,
    form,
    orderServiceTypes,
    setEndDate,
    setNoEndDate,
    setStartDate,
    type,
  ]);

  /**
   * Effect hook to automatically set default values for client frame and tariff symbols
   * when a client from the 'revaha' category is selected and there's no active order.
   */
  useEffect(() => {
    // Check if the selected client is from the 'revaha' category and there's no active order
    if (selectedClient?.category?.revaha && !activeOrder?.id) {
      // Extract client frame and tariff symbols from the selected client
      const clientFrameSymbols = selectedClient?.client_frame_symbols;
      const clientTariffSymbols = selectedClient?.client_tariff_symbols;

      // If there's exactly one client frame symbol, set it as the default
      if (clientFrameSymbols?.length === 1) {
        form.setFieldValue("client_frame_symbol_id", clientFrameSymbols[0].id);
      }

      // If there's exactly one client tariff symbol, set it as the default
      if (clientTariffSymbols?.length === 1) {
        form.setFieldValue(
          "client_tariff_symbol_id",
          clientTariffSymbols[0].id
        );
      }
    }
  }, [selectedClient, activeOrder, form]);

  useEffect(() => {
    form.validateFields();
  }, [form, activeOrder]);

  const updateFieldValue = (key, value) => {
    form.setFieldsValue({ [key]: value });
    const currentValues = form.getFieldsValue();
    onFormChange(form, key, { ...currentValues, [key]: value });
  };

  useEffect(() => {
    if (
      selectedClient != null &&
      selectedClient.category_id !== selectedCategory
    ) {
      updateFieldValue("client_id", null);
    }
  }, [selectedCategory, selectedClient, form]);

  useEffect(() => {
    if (selectedClient == null) {
      updateFieldValue("service_type", null);
    }
  }, [selectedClient, form]);

  useEffect(() => {
    const serviceTypeId =
      changedValues?.order_service_type_id || activeOrder.order_service_type_id;
    const selectedOrderServiceType = orderServiceTypes.find(
      (i) => i.id === serviceTypeId
    );
    // Determine if the selected order service type is supporting community.
    const isSupporting = selectedOrderServiceType?.supporting_community;
    const dayCenter = selectedOrderServiceType?.name === "מרכז יום";
    // Update the state.
    setIsSupportingCommunity(isSupporting);
    setIsDayCenter(dayCenter);
  }, [
    orderServiceTypes,
    changedValues?.order_service_type_id,
    activeOrder.order_service_type_id,
  ]);

  const getFormDefaults = useCallback(
    (isDuplicate, flattenOrder, serviceTypeName) => {
      return {
        effective_from: isDuplicate ? null : flattenOrder.effective_from,
        effective_to: isDuplicate ? null : flattenOrder.effective_to,
        order_close_reason_id: isDuplicate
          ? null
          : flattenOrder.order_close_reason_id,
        no_end_date: !flattenOrder.effective_to && type === "edit",
        order_service_type_id: isDuplicate
          ? flattenOrder.order_service_type_id
          : flattenOrder.order_service_type_id,
        reference_number: isDuplicate ? null : flattenOrder.reference_number,
        commitment_number: isDuplicate ? null : flattenOrder.commitment_number,
      };
    },
    [type]
  );

  const disabledDate = (current) => {
    return current && current <= moment(startDate);
  };

  const disabledFromDate = (current) => {
    // If no current date is provided, don't disable the date.
    if (!current) return false;

    // Check if the current date is after the already set end date.
    // If an end date exists and the current date is after it, this date should be disabled.
    const isAfterEndDate =
      endDate && current.isAfter(moment(endDate).endOf("day"));

    // Determine if the form is being used to duplicate an order and if the 'closed_previous_order_id' has been changed.
    // This indicates special handling for duplicated orders.
    const isDuplicateWithClosedOrder =
      type === "duplicate" && changedValues["closed_previous_order_id"];

    // If duplicating and the current date is before the start of the active order, disable this date.
    // This prevents users from selecting a date before the active order's start when duplicating.
    const isBeforeActiveOrderStart =
      isDuplicateWithClosedOrder &&
      current.isBefore(moment(activeOrder.effective_from).endOf("day"));

    // Similarly, if duplicating and the current date is after the end of the active order, disable this date.
    // This prevents users from selecting a date after the active order's end when duplicating.
    const isAfterActiveOrderEnd =
      isDuplicateWithClosedOrder &&
      activeOrder.effective_to &&
      current.isAfter(
        moment(activeOrder.effective_to).add(1, "days").endOf("day")
      );

    // If any of the conditions for disabling the date are met, return true to disable the date.
    // Otherwise, return false, allowing the date to be selected.
    return isAfterEndDate || isBeforeActiveOrderStart || isAfterActiveOrderEnd;
  };

  return {
    disabledDate,
    disabledFromDate,
    isSupportingCommunity,
    isDayCenter,
    updateFieldValue,
  };
};
